import { request } from "../api";
import type { SessionUser } from "../auth/session";
import { getJson, sendJson } from "./http";
import type { Account, AccountUpdateRecord, AccountUpdatesResponse, Comment, CommentVisibility, ContactInput, CreateAccountInput, CreateMemberInput, CreateMilestoneInput, CreateRequestInput, CreateTagInput, Dashboard, DirectoryResponse, MilestoneDetail, MilestoneSummary, RecordAccountUpdateInput, RequestDetail, RequestStatus, RequestSummary, Tag, TeamMember, UpdateAccountInput, UpdateMemberInput, UpdateMilestoneInput, UpdateTagInput } from "./types";

/**
 * Typed data access for the screens' route loaders.
 *
 * Wraps the shared request() helper (single-flight refresh, NO_APP_PERMISSION
 * redirect) and turns its {status,data} into either data or a thrown Response.
 * A loader that throws a Response hands control to the router's errorElement —
 * a 404 becomes the NotFound screen without the component testing for it.
 */
function get<T>(path: string): Promise<T> {
  return getJson<T>(request, path);
}

/** POST/PATCH/DELETE with a JSON body, through the staff request(). */
function send<T>(path: string, method: "POST" | "PATCH" | "DELETE", body: unknown, fail: string): Promise<T> {
  return sendJson<T>(request, path, method, body, fail);
}

interface RequestsResponse {
  requests: RequestSummary[];
}
interface RequestResponse {
  request: RequestDetail;
}
interface AccountsResponse {
  accounts: Account[];
}
interface AccountResponse {
  account: Account;
}

export interface RequestListOptions {
  /** Technical owner. */
  owner?: string;
  /** Account lead. */
  lead?: string;
  /** Either role — "My work". */
  member?: string;
  account?: string;
  status?: string;
  priority?: number;
  tag?: string;
  /** YYYY-MM-DD, inclusive. */
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  neededFrom?: string;
  neededTo?: string;
  /** priority (default), next_asc or next_desc. */
  sort?: string;
}

export async function fetchRequests(opts: RequestListOptions = {}): Promise<RequestSummary[]> {
  const params = new URLSearchParams();
  if (opts.owner) params.set("owner", opts.owner);
  if (opts.lead) params.set("lead", opts.lead);
  if (opts.member) params.set("member", opts.member);
  if (opts.account) params.set("account", opts.account);
  if (opts.status) params.set("status", opts.status);
  if (opts.priority !== undefined) params.set("priority", String(opts.priority));
  if (opts.tag) params.set("tag", opts.tag);
  if (opts.createdFrom) params.set("created_from", opts.createdFrom);
  if (opts.createdTo) params.set("created_to", opts.createdTo);
  if (opts.updatedFrom) params.set("updated_from", opts.updatedFrom);
  if (opts.updatedTo) params.set("updated_to", opts.updatedTo);
  if (opts.neededFrom) params.set("needed_from", opts.neededFrom);
  if (opts.neededTo) params.set("needed_to", opts.neededTo);
  if (opts.sort) params.set("sort", opts.sort);
  const qs = params.toString();
  const { requests } = await get<RequestsResponse>(`/api/requests${qs ? `?${qs}` : ""}`);
  return requests;
}

export async function fetchRequest(id: string): Promise<RequestDetail> {
  const { request: r } = await get<RequestResponse>(`/api/requests/${encodeURIComponent(id)}`);
  return r;
}

/** The signed-in person, including which team member they are. */
export async function fetchMe(): Promise<SessionUser> {
  return (await get<{ user: SessionUser }>("/api/me")).user;
}

/** The This-week screen's data, computed server side. */
export function fetchDashboard(): Promise<Dashboard> {
  return get<Dashboard>("/api/dashboard");
}

export async function fetchAccounts(): Promise<Account[]> {
  const { accounts } = await get<AccountsResponse>("/api/accounts");
  return accounts;
}

export async function fetchAccount(id: string): Promise<Account> {
  const { account } = await get<AccountResponse>(`/api/accounts/${encodeURIComponent(id)}`);
  return account;
}

/** POST a new request. Returns the created detail. Throws on a non-2xx so the
 *  caller can surface it. */
export async function createRequest(input: CreateRequestInput): Promise<RequestDetail> {
  return (await send<RequestResponse>("/api/requests", "POST", input, "Could not create request")).request;
}

/** Add a comment to a request. Returns the full thread. Throws on non-2xx. */
export async function addComment(id: string, input: { body: string; visibility: CommentVisibility }): Promise<Comment[]> {
  return (await send<{ comments: Comment[] }>(`/api/requests/${encodeURIComponent(id)}/comments`, "POST", input, "Could not add comment")).comments;
}

/** PATCH a request. Returns the updated detail. Throws on non-2xx. */
export async function updateRequest(
  id: string,
  patch: {
    status?: string;
    /** A customer-visible note written with the status change; required by the server for Waiting. */
    customerNote?: string;
    ownerId?: string | null;
    accountId?: string;
    accountLeadId?: string | null;
    finalOwner?: string | null;
    problem?: string;
    nextDate?: string | null;
    title?: string;
    description?: string;
    priority?: number;
    portalVisible?: boolean;
    type?: string;
    tagIds?: string[];
    milestoneIds?: string[];
    archived?: boolean;
    source?: string;
    sourceLabel?: string;
    order?: number | null;
    neededBy?: string | null;
    estimateDays?: number | null;
    startedAt?: string | null;
  },
): Promise<RequestDetail> {
  return (await send<RequestResponse>(`/api/requests/${encodeURIComponent(id)}`, "PATCH", patch, "Could not update request")).request;
}

/** POST a new account. Returns the created account. Throws on non-2xx. */
export async function createAccount(input: CreateAccountInput): Promise<Account> {
  return (await send<AccountResponse>("/api/accounts", "POST", input, "Could not create account")).account;
}

export async function updateAccount(id: string, patch: UpdateAccountInput): Promise<Account> {
  return (await send<AccountResponse>(`/api/accounts/${encodeURIComponent(id)}`, "PATCH", patch, "Could not update account")).account;
}

export async function addContact(accountId: string, contact: ContactInput): Promise<Account> {
  return (await send<AccountResponse>(`/api/accounts/${encodeURIComponent(accountId)}/contacts`, "POST", contact, "Could not add contact")).account;
}

export async function updateContact(contactId: number, contact: ContactInput): Promise<Account> {
  return (await send<AccountResponse>(`/api/contacts/${contactId}`, "PATCH", contact, "Could not update contact")).account;
}

export async function deleteContact(contactId: number): Promise<Account> {
  return (await send<AccountResponse>(`/api/contacts/${contactId}`, "DELETE", undefined, "Could not delete contact")).account;
}

/** Whitelist a customer-portal login domain (admin only). Throws a Response
 *  carrying the server's reason on 409/422. */
export async function addDomain(accountId: string, domain: string): Promise<Account> {
  return (await send<AccountResponse>(`/api/accounts/${encodeURIComponent(accountId)}/domains`, "POST", { domain }, "Could not add domain")).account;
}

/** Revoke a customer-portal login domain (admin only). */
export async function removeDomain(accountId: string, domain: string): Promise<Account> {
  return (await send<AccountResponse>(`/api/accounts/${encodeURIComponent(accountId)}/domains/${encodeURIComponent(domain)}`, "DELETE", undefined, "Could not remove domain")).account;
}

/** Re-exported for callers that build filters. */
export type { RequestStatus };

export async function fetchTeam({ includeLeft = false }: { includeLeft?: boolean } = {}): Promise<TeamMember[]> {
  return (await get<{ team: TeamMember[] }>(includeLeft ? "/api/team?include=left" : "/api/team")).team;
}

/** Mark a member as having left the team, or bring them back. The server
 *  refuses leaving while work still points at them; the message says what. */
export async function setMemberLeft(id: string, left: boolean): Promise<TeamMember> {
  return (await send<{ member: TeamMember }>(`/api/team/${encodeURIComponent(id)}`, "PATCH", { left }, "Could not update member")).member;
}

export async function addMember(input: CreateMemberInput): Promise<TeamMember> {
  return (await send<{ member: TeamMember }>("/api/team", "POST", input, "Could not add member")).member;
}

/** The Hub people Settings › Team can add (admin only). `people` is null when
 *  the Hub could not be read; the reason is in `error`. Loading it also
 *  refreshes the roster's Hub links (photos) server side. */
export async function fetchTeamDirectory(): Promise<DirectoryResponse> {
  return await get<DirectoryResponse>("/api/team/directory");
}

/** Add a member from the Hub: name, email and photo come from the Hub. */
export async function addMemberFromHub(hubUserId: string, role: string): Promise<TeamMember> {
  return (await send<{ member: TeamMember }>("/api/team", "POST", { hubUserId, role }, "Could not add member")).member;
}

export async function updateMember(id: string, patch: UpdateMemberInput): Promise<TeamMember> {
  return (await send<{ member: TeamMember }>(`/api/team/${encodeURIComponent(id)}`, "PATCH", patch, "Could not update member")).member;
}

export async function fetchTags(): Promise<Tag[]> {
  return (await get<{ tags: Tag[] }>("/api/tags")).tags;
}

export async function createTag(input: CreateTagInput): Promise<Tag> {
  return (await send<{ tag: Tag }>("/api/tags", "POST", input, "Could not create tag")).tag;
}

export async function updateTag(id: string, patch: UpdateTagInput): Promise<Tag> {
  return (await send<{ tag: Tag }>(`/api/tags/${encodeURIComponent(id)}`, "PATCH", patch, "Could not update tag")).tag;
}

/** Which milestones a screen is asking for. `status` takes a single status or
 *  "all"; omitted means the API's own default of planned only. */
export interface MilestoneListOptions {
  account?: string;
  status?: string;
  from?: string;
  to?: string;
}

export async function fetchMilestones(opts: MilestoneListOptions = {}): Promise<MilestoneSummary[]> {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(opts)) if (v) p.set(k, v);
  const query = p.toString();
  return (await get<{ milestones: MilestoneSummary[] }>(`/api/milestones${query ? `?${query}` : ""}`)).milestones;
}

export async function fetchMilestone(id: string): Promise<MilestoneDetail> {
  return (await get<{ milestone: MilestoneDetail }>(`/api/milestones/${id}`)).milestone;
}

export async function createMilestone(input: CreateMilestoneInput): Promise<MilestoneDetail> {
  return (await send<{ milestone: MilestoneDetail }>("/api/milestones", "POST", input, "Could not create milestone")).milestone;
}

export async function updateMilestone(id: string, patch: UpdateMilestoneInput): Promise<MilestoneDetail> {
  return (await send<{ milestone: MilestoneDetail }>(`/api/milestones/${id}`, "PATCH", patch, "Could not update milestone")).milestone;
}

/** The account's customer-update draft and the updates published so far. */
export async function fetchAccountUpdates(id: string): Promise<AccountUpdatesResponse> {
  return get<AccountUpdatesResponse>(`/api/accounts/${encodeURIComponent(id)}/updates`);
}

/** Publish an update: the text as shown, covering up to the draft's generatedAt. */
export async function recordAccountUpdate(id: string, input: RecordAccountUpdateInput): Promise<AccountUpdateRecord> {
  return (await send<{ sent: AccountUpdateRecord }>(`/api/accounts/${encodeURIComponent(id)}/updates`, "POST", input, "Could not publish the update")).sent;
}
